import React, { useMemo, useState } from 'react';
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import FieldsInitializer from '../../components/ModelDataFields/Input/Initializer';
import CalculationResultsSplitView from '../../components/CalculationResults/CalculationResultsSplitView';
import { useScenarioOutputRoot, useSensitivityData } from '../../shared/hooks/queries/useOutputData';

// Helper: Map raw API model data to the format the tree expects
const mapModelData = (modelData: any): any => {
    return {
        id: modelData.id.toString(),
        name: modelData.name,
        type: modelData.type,
        kind: modelData.kind,
        position: modelData.position,
        childrens: (modelData.childrens || []).map((e: any) => e.toString()),
        possibleChildrens: modelData.possible_childrens || [],
        childrensDefinition: modelData.childrens_definition || [],
        parentId: modelData.parent_id ? modelData.parent_id.toString() : null,
        relatedModelId: modelData.related_model_id,
        fields: FieldsInitializer(modelData.fields || {}),
        has_related_output: modelData.has_related_output,
        relatedModelDataId: modelData.related_model_id,
        isInconsiderable: modelData.is_inconsiderable,
        isDeletable: modelData.is_deletable,
        expanded: false,
        editing: false,
        anchorEl: null,
        sensitivityModelData: null,
        deleteModalOpen: false,
        detailsModalOpen: false,
        documentation: modelData.documentation,
    };
};

const mapModelDataList = (modelDataList: any[]): any => {
    const result: any = {};
    for (const elem of modelDataList) {
        result[elem.id.toString()] = mapModelData(elem);
    }
    return result;
};

const findRootItemId = (modelDataList: any[]): string => {
    const root = modelDataList.find((item) => item.parent_id == null);
    return root ? root.id.toString() : '42';
};

// Wrapper component: uses ModelContext for reactive scenario/sensitivity
export default function CalculationResults() {
    const { model, currentScenario, currentSensitivity, currentEntity, computeGeneration } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }

    return <CalculationResultsContent currentEntity={currentEntity} model={model} currentSensitivity={currentSensitivity} computeGeneration={computeGeneration} />;
}

// Content component: business logic and rendering
function CalculationResultsContent({ currentEntity, model, currentSensitivity, computeGeneration }) {
    const [mounted, setMounted] = useState(false);
    const [userRights, setUserRights] = useState({ right_to_write: false });

    const scenarioId = currentEntity.type === 'scenario' ? currentEntity.id : currentEntity.scenario_id;

    const { data: outputRootData, isLoading: isLoadingOutput } = useScenarioOutputRoot(
        scenarioId ?? 0,
        computeGeneration,
    );

    const sensitivityId = currentEntity.type === 'sensitivity' ? currentEntity.id : 0;
    const { data: sensData, isLoading: isLoadingSensitivity } = useSensitivityData(sensitivityId, computeGeneration);

    const loading = isLoadingOutput || (sensitivityId > 0 && isLoadingSensitivity);

    // Build state by merging output root with sensitivity data
    const currentState = useMemo(() => {
        if (!outputRootData) {
            return { Input: { tree: { items: {}, rootId: '42' } }, Output: { tree: { items: {}, rootId: '42' } } };
        }

        let mappedOutputItems = mapModelDataList(outputRootData.output || []);

        // Merge sensitivity output if applicable
        if (sensData?.output) {
            sensData.output.forEach((sensObj: any) => {
                const id = sensObj.id.toString();
                if (mappedOutputItems[id]) {
                    const mergedFields = { ...mappedOutputItems[id].fields };
                    Object.entries(sensObj.fields || {}).forEach(([key, field]: [string, any]) => {
                        mergedFields[key] = {
                            ...mergedFields[key],
                            value: field.value,
                            compute_error: field.compute_error,
                        };
                    });
                    mappedOutputItems[id] = {
                        ...mappedOutputItems[id],
                        fields: FieldsInitializer(mergedFields),
                    };
                }
            });
        }

        return {
            Input: { tree: { items: {}, rootId: '42' } },
            Output: { tree: { items: mappedOutputItems, rootId: findRootItemId(outputRootData.output || []) } }
        };
    }, [outputRootData, sensData]);

    const rootOutputItem = useMemo(() => {
        const rootId = currentState['Output'].tree.rootId;
        return currentState['Output'].tree.items[rootId] || null;
    }, [currentState]);

    const outputChildrenIds = useMemo(() => {
        return rootOutputItem?.childrens || [];
    }, [rootOutputItem]);

    const reloadState = () => {
        setMounted(prevState => !prevState);
    };

    const sensitivityLabel = currentSensitivity ? ` / ${currentSensitivity.name}` : '';

    return (
        <div style={{ height: "100%", overflow: "hidden" }}>
        <div className="flex flex-col h-full">
            <FMEPageHeader
                title="Calculation Results"
                breadcrumb={[model.name]}
                description="Explore detailed computation outputs."
                actions={<ScenarioSensitivitySelector />}
            />

            <div className="flex-1 min-h-0 overflow-hidden relative">
                {loading && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-40 transition-opacity duration-200">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-slate-500 font-medium">Loading data...</span>
                        </div>
                    </div>
                )}

                {outputChildrenIds.length > 0 ? (
                    <CalculationResultsSplitView
                        currentState={currentState}
                        reloadState={reloadState}
                        userRights={userRights}
                        scenarioId={currentEntity.type === 'scenario' ? currentEntity.id : currentEntity.scenario_id}
                    />
                ) : !loading ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-[32px] text-slate-400">calculate</span>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No calculation results available</h3>
                        <p className="text-slate-500 max-w-md">
                            Please design your architecture and complete all required inputs to see the computed results.
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
        </div>
    );
}
